import React, { useState } from 'react';
import './Newsletter.css';
import { useForm, SubmitHandler } from 'react-hook-form';
import ShinyButton from '../ShinyButton/ShinyButton';
import { useRecaptcha } from '../../../hooks/useRecaptcha';
import { getApiUrl, API_ENDPOINTS } from '../../../utils/api';

interface NewsletterFormData {
  email: string;
  honeypot: string;
}

export interface ReCAPTCHAProps {
  sitekey: string;
  onChange?: (value: string | null) => void;
  onErrored?: () => void;
  onExpired?: () => void;
  size?: 'compact' | 'normal' | 'invisible';
  theme?: 'light' | 'dark';
  badge?: 'bottomright' | 'bottomleft' | 'inline';
  hl?: string;
}

const Newsletter: React.FC = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<NewsletterFormData>();
  const [feedback, setFeedback] = useState<
    { type: 'success' | 'error'; message: string } | null
  >(null);

  const { isLoaded, isReady } = useRecaptcha();

  const onSubmit: SubmitHandler<NewsletterFormData> = async (data) => {
    console.log('📝 Début soumission newsletter:', { email: data.email });
    setFeedback(null);
    
    // Vérifier que reCAPTCHA est prêt
    if (!isReady || !window.grecaptcha) {
      setFeedback({
        type: 'error',
        message: 'reCAPTCHA n\'est pas encore prêt. Veuillez patienter quelques secondes et réessayer.',
      });
      return;
    }
    
    try {
      console.log('🤖 Génération token reCAPTCHA...');
      const token = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY!, {
        action: 'newsletter_submit'
      }).catch((error: any) => {
        console.error('❌ Erreur génération token reCAPTCHA:', error);
        throw new Error('Erreur lors de la génération du token reCAPTCHA');
      });
      console.log('✅ Token reCAPTCHA généré');

      const apiUrl = getApiUrl(API_ENDPOINTS.NEWSLETTER);
      
      console.log('🚀 Envoi requête au serveur:', apiUrl);
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: data.email,
          recaptchaToken: token,
          honeypot: data.honeypot,
        }),
      });

      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.message || 'Erreur lors de la soumission');
      }

      console.log('✅ Newsletter soumise avec succès:', responseData);
      setFeedback({
        type: 'success',
        message: responseData.message || 'Un email de confirmation vous a été envoyé.',
      });
      reset();

    } catch (error) {
      console.error('❌ Erreur critique:', error);
      setFeedback({
        type: 'error',
        message: error instanceof Error ? error.message : 'Une erreur est survenue. Veuillez réessayer plus tard.',
      });
    }
  };

  return (
    <section className="newsletter-section">
      <div className="blur-card">
        <h2 className="newsletter-title">Inscrivez-vous à notre newsletter</h2>
        <p className="newsletter-description">
          Recevez les dernières mises à jour et offres directement dans votre
          boîte mail.
        </p>
        <form className="newsletter-form" onSubmit={handleSubmit(onSubmit)}>
          <input
            type="text"
            className="newsletter-honeypot"
            {...register('honeypot')}
            style={{ display: 'none' }}
            autoComplete="off"
          />

          <input
            type="email"
            placeholder="Votre adresse email"
            className="newsletter-input"
            {...register('email', {
              required: "L'adresse email est requise.",
              pattern: {
                value: /^\S+@\S+$/i,
                message: 'Veuillez entrer une adresse email valide.',
              },
            })}
          />
          <ShinyButton type="submit" disabled={isSubmitting || !isReady}>
            {isSubmitting 
              ? "Envoi en cours..." 
              : !isReady 
                ? "Chargement..." 
                : "S'inscrire"
            }
          </ShinyButton>
        </form>
        {feedback && (
          <p
            className={`feedback-message ${
              feedback.type === 'success'
                ? 'feedback-success'
                : 'feedback-error'
            }`}
          >
            {feedback.message}
          </p>
        )}
        {errors.email && (
          <p className="feedback-message feedback-error">
            {errors.email.message}
          </p>
        )}
      </div>
    </section>
  );
};

export default Newsletter;